import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Database } from '@rbf/shared';
import { createClient } from '@supabase/supabase-js';
import { Platform } from 'react-native';

import { resolveDevHost } from './dev-host';
import { consumeRecoverySession, markRecoverySession } from './recovery';

const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
const supabasePublishableKey = process.env.EXPO_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabasePublishableKey) {
  throw new Error(
    'Missing Supabase config. Set EXPO_PUBLIC_SUPABASE_URL and ' +
      'EXPO_PUBLIC_SUPABASE_PUBLISHABLE_KEY (see apps/mobile/.env.example).',
  );
}

const resolvedUrl = resolveDevHost(supabaseUrl);

/**
 * The AsyncStorage key supabase persists the session under.
 *
 * Set explicitly so it is OURS to name and to delete — `auth.tsx` has to drop this key by
 * hand when supabase's own sign-out cannot (see `purgePersistedSession`), and sniffing for
 * a key the library owns is a guess. Supabase's chunked writes append `.0`, `.1`, … to it.
 *
 * The value reproduces supabase-js's own default (`sb-<first hostname label>-auth-token`,
 * from the RESOLVED url) byte for byte, and deliberately so: changing it would orphan the
 * token of every already-signed-in user and silently sign them all out on upgrade.
 */
export const AUTH_STORAGE_KEY = `sb-${new URL(resolvedUrl).hostname.split('.')[0]}-auth-token`;

export const supabase = createClient<Database>(resolvedUrl, supabasePublishableKey, {
  auth: {
    storage: AsyncStorage,
    storageKey: AUTH_STORAGE_KEY,
    autoRefreshToken: true,
    persistSession: true,
    // On web, let supabase-js parse the session out of the URL — the password
    // recovery link returns with the session in the URL (implicit-flow hash),
    // which fires PASSWORD_RECOVERY for the reset screen. On native there is no
    // document URL to parse; redirects arrive as deep links handled manually in
    // components/domain/auth/NewPasswordForm.tsx, so detection stays off.
    detectSessionInUrl: Platform.OS === 'web',
  },
});

/**
 * Record a password-recovery session the moment it is established, so the reset
 * screen can gate the password change on it.
 *
 * This listener MUST live here, at module load, and NOT in the reset screen. On web
 * supabase-js parses the recovery hash inside `_initialize()` — during `createClient`
 * above — and fires `PASSWORD_RECOVERY` synchronously-ish, then clears the URL hash;
 * a listener registered later, when `/reset/new-password` mounts, misses the event
 * outright and the signal can't be recovered. Subscribing here, immediately after
 * the client exists, is the only point guaranteed to be in place before the event.
 *
 * The mark is dropped on sign-out: it authorizes a password change on the session it
 * was established for, so it must not survive that session.
 */
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'PASSWORD_RECOVERY') {
    if (session) markRecoverySession(session.user.id);
  } else if (event === 'SIGNED_OUT') {
    consumeRecoverySession();
  }
});
